<script lang="ts" module>
  import { createPerf } from "@/libs/telemetry/performance"
  import { writable } from "svelte/store"
  import { type Preset, create } from "../preset"

  // face-180 has no upload and back_camera options
  export const ALLOWED_SOURCES: Source[] = [SOURCE.FRONT_CAMERA, SOURCE.COMPANION]

  const preset = writable<Preset>()
  const loading = writable(0)
</script>

<script lang="ts">
  import { SOURCE, type Source } from "@/config"
  import { adjustDetectionBoundingBoxToVisibleRect } from "@/features/presets/utils"
  import { readPixels } from "@/libs/cv/modules/read-pixels/read-pixels"
  import { getFeaturesStore } from "@/libs/feature-toggle/store"
  import type { ImageSource } from "@/libs/image"
  import { mix } from "@/libs/math"
  import { analytics } from "@/libs/telemetry"
  import { LoadingSplashScreen } from "@/libs/ui"
  import VideoFeed, { type Dimensions } from "@/libs/ui/VideoFeed/VideoFeed.svelte"
  import { getApplicationStore } from "@/stores/application"
  import { type ComponentProps, onMount } from "svelte"
  import { create as createCameraPermissionTracker } from "../../camera-permission-tracker"
  import { sendAnalytics } from "../analytics"
  import { checkFront, checkLeft, checkRight } from "../checks"
  import { type Detections } from "../preset"
  import { STAGE } from "../stages"
  import QualityCheckStages from "./QualityCheckStages"
  import { notify } from "./utils"

  const COMPLETE_DELAY = 2000

  let {
    stream,
    facing = "front",
    onCapture,
    onComplete,
    onError,
    onReady,
  }: {
    stream: ComponentProps<typeof VideoFeed>["src"]
    facing?: ComponentProps<typeof QualityCheckStages>["facing"]
    onReady(): void
    onComplete(): void
    onError(err: Error): void
    onCapture(
      frame: Awaited<ReturnType<NonNullable<typeof video>["getFrame"]>> & {
        metadata: { side: "front" | "left" | "right" }
      },
    ): void
  } = $props()

  const applicationStore = getApplicationStore()
  const featuresStore = getFeaturesStore()

  let video = $state<VideoFeed>()
  let stage = $state<STAGE | null>(null)
  let detections = $state<Detections>({
    face: null,
    quality: { blurScore: 0.1, noiseScore: 0.2, resolutionScore: 0.3, fullFaceVisible: true },
    illumination: { azimuth: 0, intensity: 1, elevation: 1 },
  })
  let isVideoFeedActive = $state(false)

  let animationFrameId: ReturnType<typeof requestAnimationFrame> | null = null

  const cameraPermissionTracker = createCameraPermissionTracker()
  $effect(() => cameraPermissionTracker.update({ detections }))

  onMount(() => {
    if ($loading === 0)
      create({
        performance: createPerf(),
        onProgress: ({ complete }) => loading.set(complete),
        features: $featuresStore.features,
      })
        .then((p) => {
          preset.set(p)
          onReady()
        })
        .catch(onError)

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId)
    }
  })

  let phase = $state(0)
  let captureTimeoutId: ReturnType<typeof setTimeout> | null = null

  const capture = () => {
    const capturingStage = (
      [STAGE.CAPTURING_FRONT, STAGE.CAPTURING_LEFT, STAGE.CAPTURING_RIGHT] as (typeof stage)[]
    )[phase]
    const capturingTimeout = [2000, 1500, 1500][phase]

    if (captureTimeoutId || !capturingStage || !capturingTimeout) return

    stage = capturingStage

    captureTimeoutId = setTimeout(async () => {
      if (!video) return

      onCapture({
        ...(await video.getFrame()),
        metadata: { side: (["front", "left", "right"] as const)[phase] },
      })
      phase++
    }, capturingTimeout)
  }

  const cancelCapture = () => {
    if (captureTimeoutId) {
      clearTimeout(captureTimeoutId)
      captureTimeoutId = null
    }
  }

  let isLoading = $derived(!$preset || $loading < 1)
  const processFrame = async ({
    imageSource,
    dimensions: { width, height, visibleRect },
  }: {
    imageSource: ImageSource
    dimensions: Dimensions
  }) => {
    if (isLoading) return

    const pixels = readPixels(imageSource)
    const face = await $preset.getDetections(imageSource)

    detections = {
      ...detections,
      face: face && adjustDetectionBoundingBoxToVisibleRect(face, visibleRect, { width, height }),
    }

    if (face && pixels) {
      const illumination = await $preset.getIlluminationEstimation(face, pixels)

      detections = {
        ...detections,
        illumination: {
          ...illumination,
          // Smoothening the azimuth change to avoid rapid jumps of the direction indicator
          // During the rest of the stages, applying half of the azimuth change to the current value to avoid false-negative lighting problems
          azimuth: mix(
            detections.illumination.azimuth,
            illumination.azimuth,
            stage === STAGE.LIGHTING_ANGLE_DEVIATION ? 0.1 : 1,
          ),
        },
      }
    }
  }
  const processFrames = () =>
    (animationFrameId ??= requestAnimationFrame(() => {
      animationFrameId = null

      const v = video?.getVideo()
      const dimensions = video?.getDimensions()

      if (!v || !isVideoFeedActive || !dimensions) return

      processFrame({ imageSource: v, dimensions })
        .then(() => processFrames())
        .catch((e) => {
          isVideoFeedActive = false
          onError(e)
        })
    }))

  $effect(() => {
    const check = [checkFront, checkLeft, checkRight][phase]

    if (!check) {
      stage = STAGE.CAPTURING_DONE
      return
    }

    const problem = check({ detections, settings: $applicationStore.config.settings })

    if (phase < 4 && !problem) return capture()

    cancelCapture()

    if (stage !== problem) stage = problem
  })

  $effect(() => {
    if (phase > 0) notify()
  })
  $effect(() => {
    if (phase >= 3) setTimeout(() => onComplete(), COMPLETE_DELAY)
  })

  $effect(() => {
    if (isLoading) analytics.loading({ timestamp: Date.now() })
  })
  $effect(() => {
    if (isVideoFeedActive) processFrames()
  })
</script>

<section
  class={[
    "live-video", // anchor for external customization
    "relative size-full",
  ]}
>
  <VideoFeed
    src={stream}
    mirrored={facing === "front"}
    onStart={() => (isVideoFeedActive = true)}
    onStop={() => (isVideoFeedActive = false)}
    bind:this={video}
  />

  {#if isLoading}
    <LoadingSplashScreen class="!absolute left-0 top-0 z-10" progress={$loading} />
  {:else if isVideoFeedActive}
    <QualityCheckStages
      class="!absolute left-0 top-0 z-0 size-full"
      {stage}
      {detections}
      {facing}
      onChange={(stage) => sendAnalytics({ stage, detections })}
    />
  {/if}
</section>
